import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Card,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Grid,
  Avatar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Add, Save, Delete, Edit, CloudUpload } from '@mui/icons-material';
import { testimonialService } from '../services/api';
import { getAssetUrl } from '../config';

const emptyForm = {
  quote: '',
  author_name: '',
  author_role: '',
  display_order: '0',
  is_active: '1',
};

const emptySummary = {
  average_rating: '4.9',
  rating_label: '5 Star Rating',
  summary_text: '',
};

const TestimonialsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(emptySummary);
  const [newRow, setNewRow] = useState(emptyForm);
  const [newPhoto, setNewPhoto] = useState(null);
  const [newPreview, setNewPreview] = useState('');
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState(emptyForm);
  const [editPhoto, setEditPhoto] = useState(null);
  const [editPreview, setEditPreview] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [loadError, setLoadError] = useState('');
  const [savingSummary, setSavingSummary] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const res = await testimonialService.getAll({ include_inactive: 1 });
      const payload = res?.data?.data ?? res?.data ?? {};
      setRows(Array.isArray(payload.items) ? payload.items : []);
      if (payload.summary) {
        setSummary({
          average_rating: String(payload.summary.average_rating ?? '4.9'),
          rating_label: payload.summary.rating_label || '5 Star Rating',
          summary_text: payload.summary.summary_text || '',
        });
      }
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Failed to load testimonials';
      setLoadError(msg);
      setRows([]);
      setToast({ open: true, message: msg, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onPickImage = (file, setFile, setPreview) => {
    if (!file) return;
    setFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result || '');
    reader.readAsDataURL(file);
  };

  const buildFormData = (row, photoFile) => {
    const fd = new FormData();
    if (photoFile) fd.append('photo', photoFile);
    fd.append('quote', row.quote.trim());
    fd.append('author_name', row.author_name.trim());
    fd.append('author_role', row.author_role || '');
    fd.append('display_order', String(row.display_order === '' ? 0 : row.display_order));
    fd.append('is_active', row.is_active === '1' ? '1' : '0');
    return fd;
  };

  const handleSaveSummary = async () => {
    try {
      setSavingSummary(true);
      await testimonialService.updateSummary({
        average_rating: Number(summary.average_rating) || 4.9,
        rating_label: summary.rating_label.trim() || '5 Star Rating',
        summary_text: summary.summary_text,
      });
      setToast({ open: true, message: 'Rating summary updated', type: 'success' });
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Summary update failed', type: 'error' });
    } finally {
      setSavingSummary(false);
    }
  };

  const handleAdd = async () => {
    if (!newRow.quote.trim() || !newRow.author_name.trim()) {
      setToast({ open: true, message: 'Quote and author name are required', type: 'error' });
      return;
    }
    try {
      await testimonialService.create(buildFormData(newRow, newPhoto));
      setNewRow(emptyForm);
      setNewPhoto(null);
      setNewPreview('');
      setToast({ open: true, message: 'Testimonial added', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Add failed', type: 'error' });
    }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setEditPhoto(null);
    setEditPreview(getAssetUrl(row.photo_url));
    setEditRow({
      quote: row.quote || '',
      author_name: row.author_name || '',
      author_role: row.author_role || '',
      display_order: String(row.display_order ?? 0),
      is_active: row.is_active === 0 || row.is_active === false || row.is_active === '0' ? '0' : '1',
    });
  };

  const handleSaveEdit = async () => {
    if (!editId || !editRow.quote.trim() || !editRow.author_name.trim()) return;
    try {
      await testimonialService.update(editId, buildFormData(editRow, editPhoto));
      setEditId(null);
      setEditPhoto(null);
      setEditPreview('');
      setToast({ open: true, message: 'Testimonial updated', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Update failed', type: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await testimonialService.delete(id);
      setToast({ open: true, message: 'Testimonial deleted', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Delete failed', type: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', py: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/dashboard')} sx={{ mb: 2, color: '#2c5f2d' }}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Testimonials
        </Typography>
        <Typography variant="body2" sx={{ color: '#667085', mb: 3 }}>
          Manage the “What People Say About us” section on the About page, including the rating card on the left.
        </Typography>

        {loadError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {loadError}
          </Alert>
        )}

        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Rating summary card
          </Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Average rating"
                inputProps={{ step: '0.1', min: 0, max: 5 }}
                value={summary.average_rating}
                onChange={(e) => setSummary({ ...summary, average_rating: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Rating label"
                value={summary.rating_label}
                onChange={(e) => setSummary({ ...summary, rating_label: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Summary text"
                value={summary.summary_text}
                onChange={(e) => setSummary({ ...summary, summary_text: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button variant="contained" startIcon={<Save />} onClick={handleSaveSummary} disabled={savingSummary} sx={{ backgroundColor: '#2c5f2d' }}>
                {savingSummary ? 'Saving…' : 'Save summary'}
              </Button>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Add testimonial
          </Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={3}
                label="Quote"
                value={newRow.quote}
                onChange={(e) => setNewRow({ ...newRow, quote: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Author"
                value={newRow.author_name}
                onChange={(e) => setNewRow({ ...newRow, author_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Role"
                value={newRow.author_role}
                onChange={(e) => setNewRow({ ...newRow, author_role: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={2} md={1}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Order"
                value={newRow.display_order}
                onChange={(e) => setNewRow({ ...newRow, display_order: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={2} md={1}>
              <TextField
                select
                fullWidth
                size="small"
                label="Active"
                value={newRow.is_active}
                onChange={(e) => setNewRow({ ...newRow, is_active: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="1">Yes</option>
                <option value="0">No</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button component="label" variant="outlined" startIcon={<CloudUpload />} sx={{ mb: 1 }}>
                Photo
                <input hidden type="file" accept="image/*" onChange={(e) => onPickImage(e.target.files?.[0], setNewPhoto, setNewPreview)} />
              </Button>
              {newPreview && <Avatar src={newPreview} sx={{ width: 40, height: 40 }} />}
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" startIcon={<Add />} onClick={handleAdd} sx={{ backgroundColor: '#2c5f2d' }}>
                Add
              </Button>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ border: '1px solid #e8ecf1' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8fafb' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Photo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Quote</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Author</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length > 0 ? rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {editId === row.id ? (
                        <Box>
                          <Avatar src={editPreview} sx={{ width: 48, height: 48, mb: 1 }} />
                          <Button component="label" size="small">
                            Change
                            <input hidden type="file" accept="image/*" onChange={(e) => onPickImage(e.target.files?.[0], setEditPhoto, setEditPreview)} />
                          </Button>
                        </Box>
                      ) : (
                        <Avatar src={getAssetUrl(row.photo_url)} sx={{ width: 48, height: 48, bgcolor: '#c4a574' }} />
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      {editId === row.id ? (
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          minRows={2}
                          value={editRow.quote}
                          onChange={(e) => setEditRow({ ...editRow, quote: e.target.value })}
                        />
                      ) : row.quote}
                    </TableCell>
                    <TableCell>
                      {editId === row.id ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <TextField size="small" label="Name" value={editRow.author_name} onChange={(e) => setEditRow({ ...editRow, author_name: e.target.value })} />
                          <TextField size="small" label="Role" value={editRow.author_role} onChange={(e) => setEditRow({ ...editRow, author_role: e.target.value })} />
                        </Box>
                      ) : (
                        <>
                          <Typography sx={{ fontWeight: 600 }}>{row.author_name}</Typography>
                          <Typography variant="body2" sx={{ color: '#667085' }}>{row.author_role}</Typography>
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      {editId === row.id ? (
                        <TextField size="small" type="number" sx={{ width: 90 }} value={editRow.display_order} onChange={(e) => setEditRow({ ...editRow, display_order: e.target.value })} />
                      ) : row.display_order}
                    </TableCell>
                    <TableCell>
                      {editId === row.id ? (
                        <TextField
                          select
                          size="small"
                          value={editRow.is_active}
                          onChange={(e) => setEditRow({ ...editRow, is_active: e.target.value })}
                          SelectProps={{ native: true }}
                        >
                          <option value="1">Active</option>
                          <option value="0">Inactive</option>
                        </TextField>
                      ) : (
                        <Chip
                          size="small"
                          label={row.is_active === 0 || row.is_active === false || row.is_active === '0' ? 'Inactive' : 'Active'}
                          color={row.is_active === 0 || row.is_active === false || row.is_active === '0' ? 'default' : 'success'}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {editId === row.id ? (
                        <>
                          <IconButton color="success" onClick={handleSaveEdit}><Save /></IconButton>
                          <Button size="small" onClick={() => setEditId(null)}>Cancel</Button>
                        </>
                      ) : (
                        <>
                          <IconButton color="primary" onClick={() => startEdit(row)}><Edit /></IconButton>
                          <IconButton color="error" onClick={() => handleDelete(row.id)}><Delete /></IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#667085' }}>
                      No testimonials yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast({ ...toast, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.type} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default TestimonialsPage;
